import { LineType } from '../api.js';
import { vector3_pool, matrix_pool } from '../Math.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { TransformNode as BABYLON_TransformNode } from '@inter-ikea-betrakta-babylonjs/core/Meshes/transformNode.js';
/**
 * @deprecated
 */
export class FocusBorder {
    constructor(_object, _viewer) {
        this._object = _object;
        this._viewer = _viewer;
        this.ui_lines = []; // This array should contain 12 entries, the first 6 are white inner lines while the last 6 are black outer lines.
        this.corners = new Array(8);
        this.extended_corners = new Array(8);
        this.transformed_corners = new Array(8);
        this.transformed_points = new Array(8);
        for (let i = 0; i < 12; i++) {
            // Create all the lines we could possibly need
            this.ui_lines.push(this._viewer.create_ui_line(LineType.SOLID, { x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0 }, i >= 6 ? { r: 1, g: 1, b: 1 } : { r: 0, g: 0, b: 0 }, // TODO: Make white and black once testing is done.
            0.015, true));
        }
        // Chache all vectors/points needed.
        for (let i = 0; i < 8; i++) {
            this.corners[i] = new BABYLON_Vector3();
            this.extended_corners[i] = new BABYLON_Vector3();
            this.transformed_corners[i] = new BABYLON_Vector3();
            this.transformed_points[i] = { x: 0, y: 0 };
        }
    }
    // Note: 1000 iterations seems to take ~3-10ms but generally < 5ms when testing
    update() {
        if (!this._object.is_loaded)
            return;
        const active_camera = this._viewer.get_active_camera();
        if (!active_camera)
            return;
        const camera_position = vector3_pool.fetch().fromIVector3(active_camera.position);
        // Here we force update world transform since MinMax function just uses the cached world transform without checking dirty.. Thanks Babylon.js
        this._object.mesh_nodes.forEach((mesh) => {
            // For some reason we arent allowed to call computeWorldMatrix on AbstractMesh even though it inherits from TransformNode
            if (mesh instanceof BABYLON_TransformNode) {
                mesh.computeWorldMatrix(true);
            }
        });
        const bounds = BABYLON_Mesh.MinMax(this._object.mesh_nodes);
        const center = vector3_pool.fetch().copyFrom(bounds.min).addInPlace(bounds.max).scaleInPlace(0.5);
        const distance = BABYLON_Vector3.Distance(camera_position, center);
        const thickness = 0.007 / (4 / distance);
        const offset = thickness * 0.66;
        // We can scale bounds now, since it wont effect center anyway
        bounds.min = bounds.min.subtractFromFloats(thickness * 2, thickness * 2, thickness * 2);
        bounds.max = bounds.max.subtractFromFloats(-thickness * 2, -thickness * 2, -thickness * 2); // There is no addFromFloats, so i subtract with negative value for now.
        // Lookup table that given the screenspace point gives the corresponding corners
        // Since we build a convex hull in screen space and FocusBorder in world space
        const point_to_corners = new Map();
        const camera_matrix = matrix_pool.fetch();
        const view_matrix = matrix_pool.fetch().fromIMatrix4(active_camera.view_matrix);
        const projection_matrix = matrix_pool.fetch().fromIMatrix4(active_camera.get_projection_matrix());
        view_matrix.multiplyToRef(projection_matrix, camera_matrix);
        for (let i = 0; i < 8; i++) {
            //   6___________7
            //   /_________ /|
            // 2|         3| |
            //  | |        | |    y
            //  |          | |    |  z
            //  |4|_ _ _ _ |_|5   | /
            //  |/_________|/     |/_____x
            // 0          1
            const corner = this.corners[i].copyFromFloats((i & 0b001) ? bounds.max.x : bounds.min.x, ((i & 0b010) >> 1) ? bounds.max.y : bounds.min.y, ((i & 0b100) >> 2) ? bounds.max.z : bounds.min.z);
            const extended_corner = this.extended_corners[i].copyFromFloats((i & 0b001) ? bounds.max.x - offset : bounds.min.x + offset, ((i & 0b010) >> 1) ? bounds.max.y - offset : bounds.min.y + offset, ((i & 0b100) >> 2) ? bounds.max.z - offset : bounds.min.z + offset);
            const transformed_corner = this.transformed_corners[i];
            BABYLON_Vector3.TransformCoordinatesToRef(corner, camera_matrix, transformed_corner);
            const transformed_point = this.transformed_points[i];
            transformed_point.x = transformed_corner.x;
            transformed_point.y = transformed_corner.y;
            point_to_corners.set(transformed_point, [corner, extended_corner]);
        }
        const hull_points = convex_hull(this.transformed_points);
        if (hull_points) {
            const len = hull_points.length;
            // Hull is never more than 6 points
            for (let i = 0; i < 6; i++) {
                if (i < len) { // Visible line
                    this._viewer.update_ui_line(this.ui_lines[i], point_to_corners.get(hull_points[i])[0], point_to_corners.get(hull_points[(i + 1) % len])[0], undefined, thickness);
                    this._viewer.update_ui_line(this.ui_lines[i + 6], point_to_corners.get(hull_points[i])[1], point_to_corners.get(hull_points[(i + 1) % len])[1], undefined, thickness);
                }
                else { // Hidden line
                    this._viewer.update_ui_line(this.ui_lines[i], { x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0 }, undefined, undefined);
                    this._viewer.update_ui_line(this.ui_lines[i + 6], { x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0 }, undefined, undefined);
                }
            }
        }
        vector3_pool.return(camera_position, center);
        matrix_pool.return(camera_matrix, view_matrix, projection_matrix);
    }
    dispose() {
        for (const line_id of this.ui_lines) {
            this._viewer.delete_ui_line(line_id);
        }
        this.ui_lines.length = 0;
        // @ts-ignore
        this._object = {};
    }
}
/**
 * Find relative orientation for vectors pointing from current -> next and next -> candidate.
 * Returns 0 if current -> next and next -> candidate are parallel
 * 1 if current -> next is clockwise of next -> candidate
 * 2 if current -> next is counterclockwise next -> candidate
 */
function orientation(current, next, candidate) {
    const val = (next.y - current.y) * (candidate.x - next.x) - (next.x - current.x) * (candidate.y - next.y);
    if (val === 0)
        return 0;
    return (val > 0) ? 1 : 2;
}
function convex_hull(points) {
    const num_points = points.length;
    if (num_points < 3)
        return null;
    const hull = [];
    let leftmost_index = 0;
    for (let i = 1; i < num_points; i++) {
        if (points[i].x < points[leftmost_index].x) {
            leftmost_index = i;
        }
    }
    let current = leftmost_index;
    let candidate;
    do {
        hull.push(points[current]);
        candidate = (current + 1) % num_points;
        for (let i = 0; i < num_points; i++) {
            // If i is more counterclockwise than current candidate, then
            // update candidate
            if (orientation(points[current], points[i], points[candidate]) === 2) {
                candidate = i;
            }
        }
        // Now candidate is the most counterclockwise with respect to current
        // Set candidate as current for next iteration, so that candidate is added to
        // result 'hull'
        current = candidate;
    } while (current !== leftmost_index);
    return hull;
}
//# sourceMappingURL=FocusBorder.js.map